'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ChevronDown, Plus, Star, Youtube } from 'lucide-react';

import { Z_INDEX } from '@/lib/constants/zIndex';
import { useChannelContext } from '@/lib/hooks/useChannelContext';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

interface SidebarChannelSelectorProps {
  /** 사이드바 펼침 여부 */
  isExpanded?: boolean;
  /** 모바일 compact 모드 (헤더에 삽입 시) */
  compact?: boolean;
  className?: string;
}

// ============================================================================
// Sub-components
// ============================================================================

function ChannelAvatar({
  logoUrl,
  name,
  size = 'md',
}: {
  logoUrl?: string | null;
  name: string;
  size?: 'sm' | 'md';
}) {
  const sizeClass = size === 'sm' ? 'w-6 h-6' : 'w-8 h-8';
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';

  if (logoUrl) {
    return (
      <Image
        src={logoUrl}
        alt={name}
        width={size === 'sm' ? 24 : 32}
        height={size === 'sm' ? 24 : 32}
        className={cn(sizeClass, 'rounded-full object-cover flex-shrink-0')}
      />
    );
  }
  return (
    <div
      className={cn(
        sizeClass,
        'rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center flex-shrink-0'
      )}
    >
      <Youtube className={cn(iconSize, 'text-white')} />
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SidebarChannelSelector({
  isExpanded = true,
  compact = false,
  className,
}: SidebarChannelSelectorProps) {
  const { currentChannel, channels, isSingleChannel, isMultiChannel, hasNoChannel, setChannel } =
    useChannelContext();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 닫힘
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // ── 채널 없음 ─────────────────────────────────────────────────────────────
  if (hasNoChannel) {
    if (!isExpanded && !compact) {
      return (
        <Link
          href="/channels/create"
          className={cn(
            'flex items-center justify-center w-10 h-10 mx-auto rounded-xl',
            'border-2 border-dashed border-neutral-300 hover:border-purple-400 transition-colors',
            'text-neutral-400 hover:text-purple-500',
            className
          )}
          title="채널 만들기"
        >
          <Plus className="w-4 h-4" />
        </Link>
      );
    }

    return (
      <Link
        href="/channels/create"
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-xl',
          'border-2 border-dashed border-neutral-300 hover:border-purple-400 transition-colors',
          'text-sm font-medium text-neutral-500 hover:text-purple-600',
          compact ? 'text-xs px-2 py-1.5' : '',
          className
        )}
      >
        <Plus className="w-4 h-4 flex-shrink-0" />
        {(isExpanded || compact) && <span>채널 만들기</span>}
      </Link>
    );
  }

  // ── 접힘 상태 ─────────────────────────────────────────────────────────────
  if (!isExpanded && !compact) {
    return (
      <div ref={dropdownRef} className={cn('relative flex justify-center', className)}>
        <button
          onClick={() => isMultiChannel && setIsOpen((o) => !o)}
          className={cn(
            'flex items-center justify-center w-10 h-10 rounded-xl',
            'hover:bg-neutral-100 transition-colors',
            !isMultiChannel && 'cursor-default'
          )}
          title={currentChannel?.name ?? '채널'}
        >
          {currentChannel ? (
            <ChannelAvatar logoUrl={currentChannel.logoUrl} name={currentChannel.name} size="sm" />
          ) : (
            <Youtube className="w-5 h-5 text-neutral-400" />
          )}
        </button>

        {isOpen && isMultiChannel && (
          <DropdownPanel
            channels={channels}
            currentChannelId={currentChannel?.id ?? null}
            onSelect={(id) => {
              setChannel(id);
              setIsOpen(false);
            }}
            style={{ left: '100%', top: 0, marginLeft: '8px' }}
          />
        )}
      </div>
    );
  }

  // ── compact 모드 (모바일 헤더) ────────────────────────────────────────────
  if (compact) {
    return (
      <div ref={dropdownRef} className={cn('relative', className)}>
        <button
          onClick={() => isMultiChannel && setIsOpen((o) => !o)}
          className={cn(
            'flex items-center gap-1.5 px-2 py-1.5 rounded-lg',
            'hover:bg-neutral-100 transition-colors',
            !isMultiChannel && 'cursor-default'
          )}
        >
          {currentChannel && (
            <ChannelAvatar logoUrl={currentChannel.logoUrl} name={currentChannel.name} size="sm" />
          )}
          <span className="text-sm font-semibold text-neutral-900 max-w-[100px] truncate">
            {currentChannel?.name ?? '채널 선택'}
          </span>
          {isMultiChannel && (
            <ChevronDown className={cn('w-3.5 h-3.5 text-neutral-400 transition-transform', isOpen && 'rotate-180')} />
          )}
        </button>

        {isOpen && isMultiChannel && (
          <DropdownPanel
            channels={channels}
            currentChannelId={currentChannel?.id ?? null}
            onSelect={(id) => {
              setChannel(id);
              setIsOpen(false);
            }}
            style={{ top: '100%', left: 0, marginTop: '4px' }}
          />
        )}
      </div>
    );
  }

  // ── 펼침 상태 (데스크톱) ──────────────────────────────────────────────────
  return (
    <div ref={dropdownRef} className={cn('relative px-3', className)}>
      <button
        onClick={() => isMultiChannel && setIsOpen((o) => !o)}
        className={cn(
          'flex items-center gap-2.5 w-full px-2 py-2 rounded-xl',
          'hover:bg-neutral-100 transition-colors text-left',
          isSingleChannel && 'cursor-default'
        )}
      >
        {currentChannel ? (
          <ChannelAvatar logoUrl={currentChannel.logoUrl} name={currentChannel.name} />
        ) : (
          <div className="w-8 h-8 rounded-full bg-neutral-200 flex-shrink-0" />
        )}

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-neutral-900 truncate">
            {currentChannel?.name ?? '채널 없음'}
          </p>
          {currentChannel?.isDefault && (
            <p className="text-xs text-yellow-500 flex items-center gap-0.5">
              <Star className="w-3 h-3" />
              기본 채널
            </p>
          )}
        </div>

        {isMultiChannel && (
          <ChevronDown
            className={cn(
              'w-4 h-4 text-neutral-400 flex-shrink-0 transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        )}
      </button>

      {isOpen && isMultiChannel && (
        <DropdownPanel
          channels={channels}
          currentChannelId={currentChannel?.id ?? null}
          onSelect={(id) => {
            setChannel(id);
            setIsOpen(false);
          }}
          style={{ top: '100%', left: '12px', right: '12px', marginTop: '4px' }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Dropdown Panel
// ============================================================================

function DropdownPanel({
  channels,
  currentChannelId,
  onSelect,
  style,
}: {
  channels: { id: string; name: string; logoUrl?: string | null; isDefault?: boolean }[];
  currentChannelId: string | null;
  onSelect: (id: string | null) => void;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className="absolute bg-white rounded-xl border border-neutral-200 shadow-lg py-1 min-w-[180px] max-h-64 overflow-y-auto"
      style={{ zIndex: Z_INDEX.dropdown, ...style }}
    >
      {/* 전체 보기 */}
      <button
        onClick={() => onSelect(null)}
        className={cn(
          'flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-neutral-50 transition-colors',
          currentChannelId === null && 'bg-purple-50 text-purple-700 font-medium'
        )}
      >
        <div className="w-6 h-6 rounded-full bg-neutral-200 flex items-center justify-center flex-shrink-0">
          <Youtube className="w-3.5 h-3.5 text-neutral-500" />
        </div>
        <span>전체 보기</span>
      </button>

      <div className="my-1 border-t border-neutral-100" />

      {/* 채널 목록 */}
      {channels.map((channel) => (
        <button
          key={channel.id}
          onClick={() => onSelect(channel.id)}
          className={cn(
            'flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-neutral-50 transition-colors',
            currentChannelId === channel.id && 'bg-red-50 text-red-700 font-medium'
          )}
        >
          <ChannelAvatar logoUrl={channel.logoUrl} name={channel.name} size="sm" />
          <span className="flex-1 text-left truncate">{channel.name}</span>
          {channel.isDefault && <Star className="w-3 h-3 text-yellow-400 flex-shrink-0" />}
        </button>
      ))}

      <div className="my-1 border-t border-neutral-100" />

      {/* 새 채널 */}
      <Link
        href="/channels/create"
        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700 transition-colors"
      >
        <div className="w-6 h-6 rounded-full border-2 border-dashed border-neutral-300 flex items-center justify-center flex-shrink-0">
          <Plus className="w-3 h-3" />
        </div>
        <span>새 채널 추가</span>
      </Link>
    </div>
  );
}

export default SidebarChannelSelector;
